/**
 * Fast payment-receipt PDF via jsPDF text API (no html2canvas).
 * Used for Cobros / Créditos abonos — must feel instant.
 */
import { jsPDF } from 'jspdf';
import type { Factura } from '../components/facturas/types';
import type { ReciboPagoData, ReciboPdfCompanyInfo } from './reciboPagoPdfHtml';
import { metodoPagoFacturaLabel } from './facturaPdfHtml';
import { numeroFacturaParaMostrar } from './facturaNumeroCanal';
import { canSharePdfFiles, shareFacturaPdfBlob as sharePdfBlob, downloadFacturaPdfBlob } from './facturaPdfBlob';

function formatCop(n: number): string {
  return `$${Math.round(n).toLocaleString('es-CO')}`;
}

function formatDate(d: Date): string {
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export async function buildReciboPagoPdfBlob(
  factura: Factura,
  pago: ReciboPagoData,
  info: ReciboPdfCompanyInfo
): Promise<Blob> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  let y = 18;

  const numeroMostrar = numeroFacturaParaMostrar(factura.numero, factura.canalVenta);
  const numeroRecibo = pago.numeroRecibo || `REC-${pago.pagoId.slice(0, 8).toUpperCase()}`;
  const clienteNombre = factura.cliente
    ? `${factura.cliente.nombre || ''} ${(factura.cliente as { apellido?: string })?.apellido || ''}`.trim() ||
      'Sin nombre'
    : 'Sin cliente';

  // Header bar
  pdf.setFillColor(11, 31, 74); // #0B1F4A
  pdf.rect(0, 0, pageW, 28, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(info.companyName || 'Empresa', 14, 12);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text('Recibo de pago', 14, 19);
  pdf.setFontSize(9);
  pdf.text(numeroRecibo, pageW - 14, 12, { align: 'right' });
  pdf.text(formatDate(pago.fecha), pageW - 14, 19, { align: 'right' });

  y = 38;
  pdf.setTextColor(22, 163, 74);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('PAGO RECIBIDO', 14, y);

  y += 10;
  pdf.setTextColor(21, 128, 61);
  pdf.setFontSize(22);
  pdf.text(formatCop(pago.monto), 14, y);

  y += 12;
  pdf.setDrawColor(229, 231, 235);
  pdf.line(14, y, pageW - 14, y);
  y += 10;

  pdf.setTextColor(55, 65, 81);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);

  const rows: [string, string][] = [
    ['Método de pago', metodoPagoFacturaLabel(pago.metodoPago) || pago.metodoPago || '—'],
    ['Aplicado a factura', numeroMostrar || '—'],
    ['Cliente', clienteNombre],
  ];
  if (factura.cliente?.numeroDocumento) {
    rows.push(['Documento', String(factura.cliente.numeroDocumento)]);
  }
  if (factura.cliente?.telefono) {
    rows.push(['Teléfono', String(factura.cliente.telefono)]);
  }

  for (const [label, value] of rows) {
    pdf.setTextColor(107, 114, 128);
    pdf.setFontSize(8);
    pdf.text(label.toUpperCase(), 14, y);
    pdf.setTextColor(31, 41, 55);
    pdf.setFontSize(10);
    pdf.text(String(value), 14, y + 5);
    y += 12;
  }

  y += 4;
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(14, y, pageW - 28, 36, 2, 2, 'F');
  y += 8;
  pdf.setFontSize(10);
  const summary: [string, string, boolean?][] = [
    ['Total factura', formatCop(factura.total)],
    ['Saldo anterior', formatCop(pago.saldoAnterior)],
    ['Este abono', `- ${formatCop(pago.monto)}`],
    [
      pago.saldoDespues > 0 ? 'Saldo pendiente' : 'Cuenta saldada',
      formatCop(pago.saldoDespues),
      true,
    ],
  ];
  for (const [label, value, highlight] of summary) {
    pdf.setTextColor(107, 114, 128);
    pdf.text(label, 18, y);
    if (highlight) {
      pdf.setTextColor(pago.saldoDespues > 0 ? 234 : 22, pago.saldoDespues > 0 ? 88 : 163, pago.saldoDespues > 0 ? 12 : 74);
      pdf.setFont('helvetica', 'bold');
    } else {
      pdf.setTextColor(31, 41, 55);
      pdf.setFont('helvetica', 'normal');
    }
    pdf.text(value, pageW - 18, y, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    y += 7;
  }

  if (pago.notas) {
    y += 10;
    pdf.setTextColor(107, 114, 128);
    pdf.setFontSize(8);
    pdf.text('NOTAS', 14, y);
    y += 5;
    pdf.setTextColor(55, 65, 81);
    pdf.setFontSize(9);
    const lines = pdf.splitTextToSize(String(pago.notas), pageW - 28);
    pdf.text(lines, 14, y);
    y += lines.length * 5;
  }

  y = Math.max(y + 16, 250);
  pdf.setDrawColor(229, 231, 235);
  pdf.line(14, y, pageW - 14, y);
  y += 8;
  pdf.setTextColor(107, 114, 128);
  pdf.setFontSize(8);
  pdf.text('Este documento es un comprobante de pago válido.', pageW / 2, y, { align: 'center' });
  y += 4;
  const contact = [info.companyEmail, info.companyPhone].filter(Boolean).join(' · ');
  if (contact) {
    pdf.text(contact, pageW / 2, y, { align: 'center' });
  }

  return pdf.output('blob');
}

export function safeReciboPdfFilename(numeroRecibo: string): string {
  const safe = String(numeroRecibo || 'recibo').replace(/[^\w-]+/g, '_');
  return `Recibo_${safe}.pdf`;
}

export function downloadReciboPdfBlob(blob: Blob, filename: string): void {
  downloadFacturaPdfBlob(blob, filename);
}

export { canSharePdfFiles };

export async function shareReciboPdfBlob(
  blob: Blob,
  filename: string,
  opts: { text?: string; title?: string }
): Promise<boolean> {
  return sharePdfBlob(blob, filename, opts);
}
